/**
 * RoomMenu provides a slide-out dialog for room management actions.
 *
 * Why: Room actions (copy link, leave room) need to be accessible from the
 * header with keyboard/gamepad navigation support. This component handles
 * dialog state and controller input integration.
 *
 * When to use: Rendered by Header to provide room management controls.
 * Integrates with focus navigation system for gamepad support.
 */
import * as React from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { cn } from "@merels/ui"
import { useTheme } from "../../../contexts/ThemeContext"
import { useFocusNavigationDep, useControllerInputDep } from "../../providers/FocusNavigationProvider/FocusNavigationProvider"
import { useSignals } from '@preact/signals-react/runtime'
import { useListNavigation } from "../../../hooks/useListNavigation"
import { RoomMenuTrigger } from "./RoomMenuTrigger"
import { RoomMenuContent } from "./RoomMenuContent"

export interface RoomMenuProps {
  roomTitle: string
  ownerName: string
  isOwner?: boolean
  imageUrl?: string | null
  videoEnabled?: boolean
  isClosed?: boolean
  opensAt?: string | null
  managed?: boolean
  onSetManaged?: (managed: boolean) => Promise<void>
  /** What the room calls a host, such as "Teacher". */
  hostTerm?: string
  /** Owner-only: name the host role. */
  onSetHostTerm?: (hostTerm: string) => Promise<void>
  curated?: boolean
  onSetCurated?: (curated: boolean) => Promise<void>
  onUpdateRoom?: (updates: { displayName?: string; imageUrl?: string; ownerName?: string; videoEnabled?: boolean; isClosed?: boolean; opensAt?: string | null }) => Promise<void>
  /** Resolves false (rather than throwing) if the clipboard write failed. */
  onCopyLink: () => Promise<boolean>
  onLeaveRoom: () => void
  className?: string
  triggerRef?: React.Ref<HTMLButtonElement>
  tabIndex?: number
  isFocused?: boolean
  /** Override app accent (e.g. active turn color). */
  accentColor?: string
  accentInkColor?: string
  backgroundColor?: string
}

export const RoomMenu = React.forwardRef<HTMLDivElement, RoomMenuProps>(
  ({ roomTitle, ownerName, isOwner, imageUrl, videoEnabled, isClosed, opensAt, managed, onSetManaged, hostTerm, onSetHostTerm, curated, onSetCurated, onUpdateRoom, onCopyLink, onLeaveRoom, className, triggerRef, tabIndex, isFocused, accentColor: accentColorProp, accentInkColor = '#ffffff', backgroundColor: backgroundColorProp }, _ref) => {
    const [open, setOpen] = React.useState(false)
    const [copied, setCopied] = React.useState(false)
    const { getAppColor } = useTheme()
    useSignals()
    const focusNavigation = useFocusNavigationDep()
    const controllerInput = useControllerInputDep()
    const isFocusNavActive = focusNavigation.isActive.value
    const appColorShades = getAppColor()
    const accentColor = accentColorProp ?? appColorShades.main
    const backgroundColor = backgroundColorProp ?? appColorShades.background

    const copyLinkButtonRef = React.useRef<HTMLButtonElement>(null)
    const leaveRoomButtonRef = React.useRef<HTMLButtonElement>(null)

    const handleCopyLink = React.useCallback(async () => {
      const succeeded = await onCopyLink()
      if (!succeeded) return
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }, [onCopyLink])

    const handleLeaveRoom = React.useCallback(() => {
      onLeaveRoom()
      setOpen(false)
    }, [onLeaveRoom])

    const menuItems = React.useMemo(() => [
      { type: 'copy' as const },
      { type: 'leave' as const },
    ], [])

    const menuNav = useListNavigation(
      menuItems,
      React.useCallback((item) => {
        if (item.type === 'copy') {
          handleCopyLink()
        } else {
          handleLeaveRoom()
        }
      }, [handleCopyLink, handleLeaveRoom]),
      open && isFocusNavActive,
    )

    // Register menu buttons
    React.useEffect(() => {
      if (open) {
        menuNav.registerItem(0, copyLinkButtonRef.current)
        menuNav.registerItem(1, leaveRoomButtonRef.current)
      }
    }, [open, menuNav])

    // Handle cancel/exit to close the menu
    React.useEffect(() => {
      if (!open || !isFocusNavActive) return

      const unsubscribe = controllerInput.subscribe((event) => {
        if (event.type === 'cancel' || event.type === 'exit') {
          setOpen(false)
        }
      })

      return unsubscribe
    }, [controllerInput, open, isFocusNavActive])

    return (
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <RoomMenuTrigger
            triggerRef={triggerRef}
            className={className}
            tabIndex={tabIndex}
            isFocused={isFocused}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={backgroundColor}
          />
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-overlay z-[10000] transition-opacity duration-300 data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
          <Dialog.Content
            className={cn(
              "fixed left-0 top-0 bottom-0 z-[10000] w-[280px] border-r border-basic shadow-xl",
              "flex flex-col p-6 gap-4 bg-background",
              "transition-transform duration-300 ease-out",
              "data-[state=open]:translate-x-0 data-[state=closed]:-translate-x-full"
            )}
          >
            <RoomMenuContent
              roomTitle={roomTitle}
              ownerName={ownerName}
              isOwner={isOwner}
              imageUrl={imageUrl}
              videoEnabled={videoEnabled}
              isClosed={isClosed}
              opensAt={opensAt}
              managed={managed}
              onSetManaged={onSetManaged}
              hostTerm={hostTerm}
              onSetHostTerm={onSetHostTerm}
              curated={curated}
              onSetCurated={onSetCurated}
              onUpdateRoom={onUpdateRoom}
              onCopyLink={handleCopyLink}
              onLeaveRoom={handleLeaveRoom}
              copied={copied}
              focusedIndex={menuNav.focusedIndex}
              copyLinkButtonRef={copyLinkButtonRef}
              leaveRoomButtonRef={leaveRoomButtonRef}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }
)
RoomMenu.displayName = "RoomMenu"
